import React, { Fragment, useMemo, useState, useRef, useEffect, useCallback } from 'react';
import delay from 'lodash/delay';
import { useSelector } from 'react-redux';
import { removeFromCache } from '@yme/api';
import { FilterSettings, SortFull, SortHalf } from '@yme/assets/icons';
import { BottomSheet, Header } from '@yme/atoms/bottom-sheet';
import { Button, CONTAINED_VARIANT, NO_ICON, SIZE_44 } from '@yme/atoms/button';
import { Dropdown } from '@yme/atoms/dropdown';
import { ClearAll, SearchInput } from '@yme/atoms/filters-elements';
import { Col, Container, Row } from '@yme/atoms/grid';
import { Pagination } from '@yme/atoms/pagination';
import { SpinnerLoader } from '@yme/atoms/spinner-loader';
import { FilterType, ProductType, SELECT_MODEL_MODAL_CASHED_DATA } from '@yme/common/constants';
import { useIsMount, useWindowSize } from '@yme/hooks/index';
import { CheckboxesDropdown, FilterDropdown, FiltersListSimpleResetStyled, FilterToggle, RadioButtonsDropdown, RangePicker, } from '@yme/molecules/filter-dropdown';
import { SectionWrapper } from '@yme/molecules/section-wrapper';
import { getPDPLinks } from '@yme/selectors/common';
import { getRTL } from '@yme/selectors/index';
import { useTranslation } from '@yme/services/i18n';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { RegularTextLevel2, RegularTextLevel3 } from '@yme/tokens/typography';
import { setDigitalData, DigitalDataMutation } from '@yme/utils/common';
import { COMMON_ANCHOR_ID_SCROLL_TO } from '@yme/utils/constants';
import { getPdpLinkByCategory } from '@yme/utils/product';
import { LocalizedLabels, NUMBER_FILTERS_IN_ROW } from '../common/constants';
import { getCountAppliedFilters, getCountAppliedValues, getOptionsForFilter, } from '../common/helpers';
import { CompareProducts, FilterResults } from './components';
import { DRIVER_LICENCE_CAMPAIGN_TEST_ID, MAX_RANGE_POINTER } from './constants';
import { getFilterLabelKey, getFilterType, getMeasurementUnit, getPlaceHolder, getPlaceholderLabelKey, } from './helpers';
import { FiltersButtonStyled, SortOrderStyled } from './styled';
import { useComparisonFilter } from './useComparisonFilter';
export function ComparisonFilter({ title, filterSettings: { isPaginationExist, perPageLimitSetting, sortOptionsType }, scrollToComponentId, productEndpoint, comparisonPagePath, categoryIds, filterFacetsConfig, cqType, endpoints, pdpPageUrl, categoryModel, defaultStartYear, productType = ProductType.UNIT, isInEditor, segments, }) {
    const { isMobileView, isTabletView, isInfiniteDesktopView } = useWindowSize();
    const [isFiltersBottomSheetOpened, setIsFiltersBottomSheetOpened] = useState(false);
    const isRightToLeft = useSelector(getRTL);
    const isMount = useIsMount();
    const pdpLinks = useSelector(getPDPLinks);
    const isTracked = useRef(false);
    const { t } = useTranslation();
    const { data, 
    // Sort Order API
    sortingProps, 
    // Cleared filter API
    filterClearedProps, 
    // Pagination API & (with perPage API)
    paginationProps, 
    // Counter API
    counter, 
    // Search API
    searchInput, loading, facetsProps, isLoaded, } = useComparisonFilter({
        categoryIds,
        productEndpoint,
        filterFacetsConfig,
        sortOptionsType,
        perPageLimitSetting,
        productType,
    });
    const sortOrderOption = useMemo(() => {
        const currentOption = sortingProps.sortFilterOptions.find((option) => option.value === sortingProps.sortOrder);
        const defaultOption = sortingProps.sortFilterOptions.find((option) => Boolean(option?.isValueDefault)) ||
            sortingProps.sortFilterOptions[0];
        return currentOption ?? defaultOption;
    }, [sortingProps.sortFilterOptions, sortingProps.sortOrder]);
    useEffect(() => {
        if (!data.length || isTracked.current) {
            return;
        }
        isTracked.current = true;
        setDigitalData(DigitalDataMutation.PLP_VIEW, {
            products: data,
            cursor: counter,
            cqType: cqType ?? '',
            pdpPageUrl: getPdpLinkByCategory(pdpLinks, data[0].categories),
            sortBy: sortOrderOption.value,
            category: categoryIds[0],
        });
    }, [data, categoryIds, counter, cqType, pdpLinks, sortOrderOption.value]);
    useEffect(() => {
        return () => {
            removeFromCache(SELECT_MODEL_MODAL_CASHED_DATA);
        };
    }, []);
    const handleOpenMobileFilters = useCallback(() => {
        setIsFiltersBottomSheetOpened(true);
    }, []);
    const handleCloseMobileFilters = useCallback(() => {
        delay(() => setIsFiltersBottomSheetOpened(false), 100);
    }, []);
    const countColumnsDesktop = filterFacetsConfig.length === NUMBER_FILTERS_IN_ROW ? 3 : 4;
    const countAppliedFilters = getCountAppliedFilters(facetsProps.facets);
    const filtersLabel = countAppliedFilters
        ? `${t('filters_filters_label')} (${countAppliedFilters})`
        : t('filters_filters_label');
    const hasFilters = Boolean(filterFacetsConfig.length);
    const facets = useMemo(() => facetsProps.facets.length
        ? facetsProps.facets
        : filterFacetsConfig.map((filterFacet) => ({
            ...filterFacet,
            values: [],
        })), [facetsProps.facets, filterFacetsConfig]);
    const Filters = useMemo(() => facets.map(({ id, values }) => {
        const filterType = getFilterType(filterFacetsConfig, id);
        const filterLabel = t(getFilterLabelKey(id));
        const measurementUnit = getMeasurementUnit(filterFacetsConfig, id);
        const maxRangeFacet = filterType === FilterType.RANGE_MIN &&
            facets.find((item) => item.id.toLowerCase().includes(MAX_RANGE_POINTER));
        const placeHolderLabel = getPlaceHolder({
            filterType,
            values,
            measurementUnit,
            rangeMax: maxRangeFacet ? maxRangeFacet?.values : undefined,
            isRightToLeft,
        });
        if (filterType === FilterType.RANGE_MAX) {
            return null;
        }
        const Filter = filterType === FilterType.TOGGLE ? (React.createElement(FilterToggle, { onClickHandler: (value) => facetsProps.handleUpdateFacets(id, [
                {
                    checked: value,
                    value: LocalizedLabels.YES,
                    label: '',
                },
            ]), isActive: Boolean(values.find((item) => item.value === LocalizedLabels.YES)?.checked), isRightToLeft: isRightToLeft, placeholderOn: t('common_enable_label'), placeholderOff: t('common_disable_label'), label: filterLabel, isListItemView: !isInfiniteDesktopView })) : (React.createElement(FilterDropdown, { options: getOptionsForFilter({
                values,
                t,
                facetId: id,
                additionalText: t('productsGrid_includesSpaceForLock_hint'),
            }), titleLabel: filterLabel, emptyPlaceholderLabel: t(getPlaceholderLabelKey(filterType)), selectedPlaceholderLabel: t('filters_optionsSelected_placeholder'), isRightToLeft: isRightToLeft, type: filterType, placeHolderLabel: placeHolderLabel, isListItemView: !isInfiniteDesktopView, countAppliedValues: getCountAppliedValues(values) },
            filterType === FilterType.CHECKLIST && (React.createElement(CheckboxesDropdown, { id: id, labels: {
                    filters_selectAll_label: t('filters_selectAll_label'),
                    filters_clearAll_label: t('filters_clearAll_label'),
                    common_confirm_button: t('common_confirm_button'),
                }, onSelect: (option) => facetsProps.handleUpdateFacets(id, option), isRightToLeft: isRightToLeft, hasAdditionalControl: isInfiniteDesktopView })),
            filterType === FilterType.RADIO && (React.createElement(RadioButtonsDropdown, { isRightToLeft: isRightToLeft, name: id, clearAllLabel: t('filters_clearAll_label'), confirmLabel: t('common_confirm_button'), onChange: (options) => facetsProps.handleUpdateFacets(id, options) })),
            (filterType === FilterType.RANGE || filterType === FilterType.RANGE_MIN) &&
                values[0] && (React.createElement(RangePicker, { isRightToLeft: isRightToLeft, dataTestId: id, min: values[0].min, max: !maxRangeFacet
                    ? values[0].max
                    : maxRangeFacet.values[0].max, low: values[0].rangeLow, high: !maxRangeFacet
                    ? values[0].rangeHigh
                    : maxRangeFacet.values[0].rangeHigh, valueSymbol: measurementUnit, confirmLabel: t('common_confirm_button'), onSubmit: (rangeValues) => facetsProps.handleUpdateRangeFacet(!maxRangeFacet ? [id] : [id, maxRangeFacet.id], rangeValues) }))));
        if (isInfiniteDesktopView) {
            return (React.createElement(Col, { lg: countColumnsDesktop, key: id }, Filter));
        }
        return React.createElement(Fragment, { key: id }, Filter);
    }), [
        countColumnsDesktop,
        facets,
        facetsProps,
        filterFacetsConfig,
        isRightToLeft,
        t,
        isInfiniteDesktopView,
    ]);
    // Common "SortOrder" Component (for all dimensions/screens)
    const SortOrderComponent = (React.createElement(SortOrderStyled, { hasFilters: hasFilters },
        React.createElement(Dropdown, { id: "sort_order", title: t('filters_sortBy_label'), isInheritBackground: true, useForMobile: true, hasRadioButton: true, leftSideIconType: isInfiniteDesktopView ? SortFull : SortHalf, isRightToLeft: isRightToLeft, items: sortingProps.sortFilterOptions, activeItem: sortOrderOption.value, onChange: sortingProps.handleSortOrderChange })));
    // Common "SearchInput" Component (for all dimensions/screens)
    const SearchComponent = (React.createElement(SearchInput, { label: t('productsGrid_productNameFilter_label'), placeholder: t('productsGrid_searchByName_placeholder'), inputValue: searchInput.searchInputValue, setInputValue: searchInput.handleSearch, isLabelVisible: isInfiniteDesktopView && hasFilters, hasFilters: hasFilters }));
    // Dynamic header area for this general component
    const HeaderRightSideComponent = (React.createElement(FlexWrapper, { gap: "12px" },
        !filterClearedProps.isFilterCleared && (React.createElement(ClearAll, { label: t('filters_clearFilters_label'), isRightToLeft: isRightToLeft, onClick: filterClearedProps.handleFilterCleared })),
        !hasFilters && SearchComponent,
        SortOrderComponent));
    // Common "FiltersButton" Component (only for mobile view/screens)
    const FiltersButton = (React.createElement(FiltersButtonStyled, { iconType: FilterSettings, isFirstIcon: true, onClick: handleOpenMobileFilters }, filtersLabel));
    //  Only for mobile view
    const MobileViewComponent = (React.createElement(React.Fragment, null,
        React.createElement(Row, null,
            React.createElement(Col, null,
                React.createElement(FlexWrapper, { mb: "12px", flexDirection: "column" }, SearchComponent)),
            React.createElement(Col, null,
                React.createElement(FlexWrapper, { gap: "8px" },
                    hasFilters && FiltersButton,
                    SortOrderComponent))),
        React.createElement(Row, null,
            React.createElement(Col, { "data-testid": DRIVER_LICENCE_CAMPAIGN_TEST_ID }))));
    // Only for tablet view
    const TabletViewComponent = (React.createElement(React.Fragment, null,
        React.createElement(Row, null,
            React.createElement(Col, null,
                React.createElement(FlexWrapper, { gap: "16px" },
                    React.createElement(FlexWrapper, { flex: hasFilters ? '60%' : '50%' }, SearchComponent),
                    hasFilters && React.createElement(FlexWrapper, { flex: "0 0 auto" }, FiltersButton),
                    React.createElement(FlexWrapper, { flex: hasFilters ? '40%' : '50%' }, SortOrderComponent)))),
        React.createElement(Row, null,
            React.createElement(Col, { "data-testid": DRIVER_LICENCE_CAMPAIGN_TEST_ID }))));
    // For desktop and lager sizes of screen
    const DesktopViewComponent = (React.createElement(React.Fragment, null,
        React.createElement(Row, { rowGap: "24px", alignItems: "flex-end" },
            Filters,
            hasFilters && React.createElement(Col, { lg: countColumnsDesktop }, SearchComponent)),
        React.createElement(Row, null,
            React.createElement(Col, { "data-testid": DRIVER_LICENCE_CAMPAIGN_TEST_ID }))));
    return (React.createElement(React.Fragment, null,
        React.createElement(Container, { width: "100%", id: scrollToComponentId },
            React.createElement(SectionWrapper, { headerLeftSide: title, isRightToLeft: isRightToLeft, isVerticalIndentDefault: false, headerRightSide: isInfiniteDesktopView && HeaderRightSideComponent },
                React.createElement(FlexWrapper, { flexDirection: "column", position: "relative" },
                    React.createElement(FlexWrapper, { mb: "56px", mbMobile: "24px", mbTablet: "32px" },
                        React.createElement(Container, { expanded: true, width: "100%" },
                            isMobileView && MobileViewComponent,
                            isTabletView && TabletViewComponent,
                            isInfiniteDesktopView && DesktopViewComponent)),
                    React.createElement(FlexWrapper, { mb: "56px" },
                        React.createElement(FilterResults, { isLoaded: isLoaded, currentPerPageValue: paginationProps.currentPerPageValue, productListData: data, pdpPageUrl: pdpPageUrl, isProductsComparisonDisabled: categoryModel?.productsComparisonDisabled, isInEditor: isInEditor, isRightToLeft: isRightToLeft, labels: {
                                titleEmptyResults: t('productsGrid_noProductsFound_label'),
                                descriptionEmptyResults: t('productsGrid_changeFilters_hint'),
                            } })),
                    isPaginationExist && !isMount && data.length > 0 && (React.createElement(Container, { expanded: true, width: "100%" },
                        React.createElement(Row, null,
                            React.createElement(Col, null,
                                React.createElement(Pagination
                                // common props
                                , { 
                                    // common props
                                    isRightToLeft: isRightToLeft, isMobileView: isMobileView, 
                                    // pagination props
                                    paginationData: paginationProps.paginationParam, paginationConfig: paginationProps.paginationConfig, activeItemValue: paginationProps.paginationActivePage, onClickItemHandler: paginationProps.onClickActivePageHandler, 
                                    // per page props
                                    perPageLabel: t('filters_itemsPerPage_label'), currentPerPageValue: paginationProps.currentPerPageValue, setCurrentPerPageValue: paginationProps.setCurrentPerPageValue, 
                                    // scrollTo feature -> after click by item in pagination
                                    scrollToElement: {
                                        id: COMMON_ANCHOR_ID_SCROLL_TO,
                                        yOffset: 160,
                                    } })))))),
                React.createElement(SpinnerLoader, { isLoading: loading }))),
        React.createElement(CompareProducts, { defaultStartYear: defaultStartYear, endpoints: endpoints, segments: segments, categoryModel: categoryModel, comparisonPagePath: comparisonPagePath }),
        React.createElement(BottomSheet, { initialFocusRef: false, open: isFiltersBottomSheetOpened, header: React.createElement(Header, { headerLeft: React.createElement(RegularTextLevel2, { weight: 500 }, filtersLabel), headerRight: !filterClearedProps.isFilterCleared ? (React.createElement(RegularTextLevel3, { as: "span", onClick: filterClearedProps.handleFilterCleared }, t('filters_clearFilters_label'))) : undefined }), footer: React.createElement(Button, { size: SIZE_44, hasIconButton: NO_ICON, buttonVariant: CONTAINED_VARIANT, label: !filterClearedProps.isFilterCleared
                    ? `${t('common_showResults_button')} ${Boolean(counter.total) ? `(${counter.total})` : ''}`
                    : t('common_showResults_button'), onClick: handleCloseMobileFilters }), onDismiss: handleCloseMobileFilters },
            React.createElement(FiltersListSimpleResetStyled, null, Filters))));
}
ComparisonFilter.displayName = 'ComparisonFilter';
